import React from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  Box,
  Button,
  FormHelperText,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@apollo/client/react';
import { useSnackbar } from 'notistack';
import {
  GET_ONBOARDING,
  SUBMIT_ONBOARDING_FROM_INSTAGRAM,
} from '../../graphql/onboarding';

const InstagramQuickStart = ({ onComplete, onSkip }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [submit] = useMutation(SUBMIT_ONBOARDING_FROM_INSTAGRAM, {
    refetchQueries: [{ query: GET_ONBOARDING }],
    awaitRefetchQueries: true,
    onError: (error) => {
      enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
    },
    onCompleted: () => {
      enqueueSnackbar('Your app has been prefilled from Instagram.', {
        variant: 'success',
      });
      if (onComplete) onComplete();
    },
  });

  return (
    <Formik
      initialValues={{ username: '' }}
      validationSchema={Yup.object().shape({
        username: Yup.string()
          .trim()
          .matches(
            /^@?[A-Za-z0-9._]{1,30}$/,
            'Enter a valid Instagram handle'
          )
          .required('Required'),
      })}
      onSubmit={async (values, { setSubmitting, setErrors }) => {
        try {
          setSubmitting(true);
          enqueueSnackbar(
            'Pulling your Instagram profile and drafting your app…',
            { autoHideDuration: 3000 }
          );
          const username = values.username.trim().replace(/^@+/, '');
          await submit({ variables: { username } });
        } catch (err) {
          setErrors({ submit: err.message });
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
      }) => (
        <form onSubmit={handleSubmit}>
          <Typography variant="h3" color="textPrimary">
            Quick Start
          </Typography>
          <Box mt={2}>
            <Typography variant="subtitle1" color="textSecondary">
              Enter your Instagram handle and we'll prefill your profile, app
              name, icon, and a starter training program from your posts. You
              can edit anything afterwards.
            </Typography>
          </Box>

          <Box mt={3}>
            <TextField
              autoFocus
              error={Boolean(touched.username && errors.username)}
              fullWidth
              helperText={touched.username && errors.username}
              label="Instagram Username"
              name="username"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.username}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">@</InputAdornment>
                ),
              }}
            />
            <FormHelperText>
              We use the bio and recent post captions to draft your app
              content.
            </FormHelperText>
          </Box>

          <Box mt={6} display="flex" alignItems="center">
            {onSkip && (
              <Button onClick={onSkip} size="large" disabled={isSubmitting}>
                Skip and fill manually
              </Button>
            )}
            <Box flexGrow={1} />
            <Button
              color="secondary"
              disabled={isSubmitting}
              type="submit"
              variant="contained"
              size="large"
            >
              {isSubmitting ? 'Drafting your app…' : 'Prefill my app'}
            </Button>
          </Box>
        </form>
      )}
    </Formik>
  );
};

InstagramQuickStart.propTypes = {
  onComplete: PropTypes.func,
  onSkip: PropTypes.func,
};

export default InstagramQuickStart;
